/**
 * Objetivos «una vez para siempre» (Finanzas · bloque I, decidido con Alex
 * el 11 oct 2026).
 *
 * Antes había que ponerlos cada mes (`TGT#<YYYY-MM>`). Ahora valen para todos
 * los meses hasta que se cambien y se guardan con `month = 'siempre'`
 * (`TGT#SIEMPRE`, `TGT#SIEMPRE#LOCATION#<id>`, y `…#LOCATION#grupo` para
 * «Los mismos objetivos para todos los locales»). Las ventas pueden ser
 * «Igual todos los meses» o «Distinto cada mes» (12 casillas).
 *
 * COMPATIBILIDAD: sin fila «siempre», manda la fila vieja del mes pedido y,
 * si no la hay, la del último mes con datos (decisión de Pol: «el del último
 * mes con datos, por local»). Nada se migra para leer; el script
 * infra/scripts/objetivos-para-siempre.mjs las pasa a «siempre» con vista
 * previa.
 *
 * Vienen PUESTOS (precargados): sin nada guardado, cada % toma su
 * recomendado. Las ventas no se precargan: se proponen (último mes cerrado
 * + 5 %, redondeado a centenas).
 */
import { consolidateTargets, inHouseRevenueOf, revenueOf } from './finance';
import { addMonths, monthOf } from './period';
import { DEFAULT_LOCATION_ID, effectiveLocationId, SHARED_LOCATION_ID, type DailyRevenue, type Target } from './types';
import { round } from './units';

/** Mes comodín de los objetivos para siempre. */
export const STANDING_TARGET_MONTH = 'siempre';

export interface Recomendado {
  valor: number;
  min: number;
  max: number;
}

/** Recomendados de Alex (30 / 30 / 60 / 15). */
export const OBJETIVOS_RECOMENDADOS = {
  foodCostPct: { valor: 30, min: 28, max: 32 },
  laborCostPct: { valor: 30, min: 25, max: 32 },
  primeCostPct: { valor: 60, min: 55, max: 65 },
  ebitdaPct: { valor: 15, min: 10, max: 20 },
} as const satisfies Record<string, Recomendado>;

export type ObjetivoPct = keyof typeof OBJETIVOS_RECOMENDADOS;
export const OBJETIVOS_PCT: ObjetivoPct[] = ['foodCostPct', 'laborCostPct', 'primeCostPct', 'ebitdaPct'];

/** Cada plato, mínimo: en el local 65 %, cada app 45 %. */
export const MINIMO_PLATO_RECOMENDADO = { local: 65, app: 45 } as const;

export const MESES_DEL_ANIO = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'] as const;

const METRICAS = ['netRevenue', 'averageTicket', 'averageTicketPerTable', 'materialPurchases', 'foodCostPct', 'laborCostPct', 'primeCostPct', 'ebitdaPct', 'safetyMarginPct'] as const;

export function isStandingTarget(t: Pick<Target, 'month'>): boolean {
  return t.month === STANDING_TARGET_MONTH;
}

function tieneDatos(t: Target): boolean {
  return METRICAS.some((m) => t[m] != null) || Object.keys(t.monthlyRevenue ?? {}).length > 0;
}

/** La fila vieja (por mes) más reciente con datos de un local. */
export function ultimoObjetivoMensual(rows: readonly Target[], locationId: string): Target | undefined {
  return rows
    .filter((t) => !isStandingTarget(t) && effectiveLocationId(t.locationId) === locationId && tieneDatos(t))
    .sort((a, b) => b.month.localeCompare(a.month))[0];
}

export type FuenteObjetivo = 'guardado' | 'todos' | 'mes' | 'ultimo-mes' | 'recomendado';

export interface ObjetivoResuelto {
  /** Lo guardado (o heredado de un mes viejo); undefined = nada, todo recomendado. */
  target?: Target;
  fuente: FuenteObjetivo;
}

/**
 * ¿Los mismos objetivos para todos los locales? Lo dice la fila «grupo». Sin
 * ella, sí, salvo que algún local ya tenga los suyos (para no esconderlos).
 */
export function mismosParaTodos(rows: readonly Target[], locationIds: readonly string[]): boolean {
  const grupo = rows.find((t) => isStandingTarget(t) && t.locationId === SHARED_LOCATION_ID);
  if (grupo?.sameForAllLocations != null) return grupo.sameForAllLocations;
  if (locationIds.length <= 1) return true;
  return !locationIds.some((id) =>
    rows.some((t) => effectiveLocationId(t.locationId) === id && tieneDatos(t)),
  );
}

/**
 * El objetivo que manda para un local en un mes:
 *  1. «Los mismos para todos» marcado (y hay más de un local) → fila grupo.
 *  2. Su fila «siempre».
 *  3. Su fila vieja de ESE mes (los meses pasados siguen con lo que tenían).
 *  4. Su última fila vieja con datos.
 *  5. La fila grupo, si existe (local nuevo de un grupo que desmarcó).
 *  6. Nada: todo recomendado.
 */
export function resolverObjetivo(
  rows: readonly Target[],
  locationId: string,
  month: string | undefined,
  locationIds: readonly string[],
): ObjetivoResuelto {
  const grupo = rows.find((t) => isStandingTarget(t) && t.locationId === SHARED_LOCATION_ID);
  if (grupo && locationIds.length > 1 && mismosParaTodos(rows, locationIds)) return { target: grupo, fuente: 'todos' };
  const propio = rows.find((t) => isStandingTarget(t) && effectiveLocationId(t.locationId) === locationId);
  if (propio) return { target: propio, fuente: 'guardado' };
  if (month) {
    const delMes = rows.find((t) => t.month === month && effectiveLocationId(t.locationId) === locationId && tieneDatos(t));
    if (delMes) return { target: delMes, fuente: 'mes' };
  }
  const ultimo = ultimoObjetivoMensual(rows, locationId);
  if (ultimo) return { target: ultimo, fuente: 'ultimo-mes' };
  if (grupo) return { target: grupo, fuente: 'todos' };
  return { fuente: 'recomendado' };
}

/** Ventas objetivo de un mes según el modo («Igual» o «Distinto cada mes»). */
export function ventasObjetivoDelMes(t: Target | undefined, month: string): number | undefined {
  if (!t) return undefined;
  if (t.salesMode === 'por_mes') {
    const v = t.monthlyRevenue?.[month.slice(5, 7)];
    return v ?? t.netRevenue;
  }
  return t.netRevenue;
}

/**
 * El objetivo efectivo de un mes: lo resuelto, con las ventas de ese mes y
 * los % que falten con su recomendado («vienen puestos»).
 */
export function objetivoDelMes(
  resuelto: ObjetivoResuelto,
  ctx: { tenantId: string; locationId?: string; month: string },
): Target {
  const base = resuelto.target;
  const out: Target = {
    ...(base ?? {}),
    tenantId: ctx.tenantId,
    month: ctx.month,
    ...(ctx.locationId ? { locationId: ctx.locationId } : {}),
  };
  if (!ctx.locationId) delete out.locationId;
  const ventas = ventasObjetivoDelMes(base, ctx.month);
  if (ventas != null) out.netRevenue = ventas;
  else delete out.netRevenue;
  for (const k of OBJETIVOS_PCT) {
    if (out[k] == null) out[k] = OBJETIVOS_RECOMENDADOS[k].valor;
  }
  delete out.sameForAllLocations;
  return out;
}

/**
 * Objetivo efectivo de una vista: un local, o «Todos los locales». En
 * consolidado: con «los mismos para todos», la fila grupo tal cual (sus
 * ventas son las del grupo); si no, el consolidado de los locales.
 */
export function objetivoDeLaVista(
  rows: readonly Target[],
  ctx: {
    tenantId: string;
    month: string;
    locationId?: string;
    locationIds: readonly string[];
    /** Ventas de los meses anteriores: reparten el objetivo de ventas «de todos» entre locales. */
    revenues?: readonly DailyRevenue[];
  },
): Target {
  const ids = ctx.locationIds.length ? ctx.locationIds : [DEFAULT_LOCATION_ID];
  if (ctx.locationId) {
    const resuelto = resolverObjetivo(rows, ctx.locationId, ctx.month, ids);
    const out = objetivoDelMes(resuelto, { tenantId: ctx.tenantId, locationId: ctx.locationId, month: ctx.month });
    // «Los mismos para todos»: los % valen igual para cada local, pero las
    // ventas son las del GRUPO: a cada local le toca su parte (si no, al
    // juntar dos locales el objetivo de ventas salía el doble).
    if (resuelto.fuente === 'todos' && ids.length > 1) {
      return repartirVentasDelGrupo(out, parteDelObjetivoDeGrupo(ctx.revenues ?? [], ctx.locationId, ids, ctx.month));
    }
    return out;
  }
  if (ids.length === 1) {
    return objetivoDelMes(resolverObjetivo(rows, ids[0], ctx.month, ids), { tenantId: ctx.tenantId, month: ctx.month });
  }
  if (mismosParaTodos(rows, ids)) {
    const grupo = rows.find((t) => isStandingTarget(t) && t.locationId === SHARED_LOCATION_ID);
    return objetivoDelMes({ target: grupo, fuente: grupo ? 'todos' : 'recomendado' }, { tenantId: ctx.tenantId, month: ctx.month });
  }
  const porLocal = ids.map((id) => objetivoDeLaVista(rows, { ...ctx, locationId: id, locationIds: ids }));
  const consolidado = consolidateTargets(porLocal) ?? { tenantId: ctx.tenantId, month: ctx.month };
  return objetivoDelMes({ target: consolidado, fuente: 'guardado' }, { tenantId: ctx.tenantId, month: ctx.month });
}

/**
 * Parte de un local en el objetivo de ventas «de todos»: lo que vendió en el
 * último mes cerrado (el último antes de `month` con ventas) entre lo que
 * vendieron todos; sin ventas, a partes iguales.
 */
export function parteDelObjetivoDeGrupo(
  revenues: readonly DailyRevenue[],
  locationId: string,
  locationIds: readonly string[],
  month: string,
): number {
  const porMes = new Map<string, { own: number; total: number }>();
  for (const d of revenues) {
    const m = monthOf(d.date);
    const id = effectiveLocationId(d.locationId);
    if (m >= month || !locationIds.includes(id)) continue;
    const b = porMes.get(m) ?? { own: 0, total: 0 };
    const v = revenueOf(d);
    b.total += v;
    if (id === locationId) b.own += v;
    porMes.set(m, b);
  }
  const ultimo = [...porMes.entries()].filter(([, b]) => b.total > 0).sort(([a], [b]) => b.localeCompare(a))[0];
  return ultimo ? ultimo[1].own / ultimo[1].total : 1 / Math.max(1, locationIds.length);
}

function repartirVentasDelGrupo(t: Target, parte: number): Target {
  const out: Target = { ...t };
  if (out.netRevenue != null) out.netRevenue = round(out.netRevenue * parte, 2);
  if (out.materialPurchases != null) out.materialPurchases = round(out.materialPurchases * parte, 2);
  if (out.monthlyRevenue) {
    out.monthlyRevenue = Object.fromEntries(Object.entries(out.monthlyRevenue).map(([k, v]) => [k, round(v * parte, 2)]));
  }
  return out;
}

/* ------------------------------------------------------------------ */
/* Propuesta de ventas                                                 */
/* ------------------------------------------------------------------ */

const centenas = (v: number) => Math.round(v / 100) * 100;

export interface PropuestaVentas {
  /** Último mes cerrado con ventas. */
  mes: string;
  vendido: number;
  /** vendido + 5 %, redondeado a centenas. */
  propuesta: number;
  /** «Distinto cada mes»: mismo mes del año pasado + 5 % (o la propuesta). */
  porMes: Record<string, number>;
}

/**
 * Regla 2 de Objetivos: «Propuesta cuando aún no hay objetivo: último mes
 * cerrado + 5 %, redondeado a centenas»; las 12 casillas, con lo vendido ese
 * mes el año anterior + 5 % (si no hay, la propuesta en todas).
 *
 * `ventasPorMes`: YYYY-MM → ventas sin IVA. `mesActual`: el mes de hoy (no
 * está cerrado).
 */
export function proponerVentas(ventasPorMes: Readonly<Record<string, number>>, mesActual: string): PropuestaVentas | undefined {
  const cerrados = Object.entries(ventasPorMes)
    .filter(([m, v]) => m < mesActual && v > 0)
    .sort(([a], [b]) => b.localeCompare(a));
  if (!cerrados.length) return undefined;
  const [mes, vendido] = cerrados[0];
  const propuesta = centenas(vendido * 1.05);
  const porMes: Record<string, number> = {};
  for (const mm of MESES_DEL_ANIO) {
    // El mismo mes del año pasado respecto al próximo que va a llegar.
    const anio = Number(mesActual.slice(0, 4));
    const proximo = `${mm >= mesActual.slice(5, 7) ? anio : anio + 1}-${mm}`;
    const anioPasado = addMonths(proximo, -12);
    const v = ventasPorMes[anioPasado];
    porMes[mm] = v && v > 0 && anioPasado < mesActual ? centenas(v * 1.05) : propuesta;
  }
  return { mes, vendido, propuesta, porMes };
}

/** Lo que se guarda en la fila «siempre» a partir de una fila vieja de un mes. */
export function objetivoParaSiempreDesde(t: Target): Target {
  const { tenantId, locationId, netRevenue, averageTicket, foodCostPct, laborCostPct, primeCostPct, ebitdaPct } = t;
  const out: Target = { tenantId, month: STANDING_TARGET_MONTH, salesMode: 'igual' };
  if (locationId) out.locationId = locationId;
  if (netRevenue != null) out.netRevenue = netRevenue;
  if (averageTicket != null) out.averageTicket = averageTicket;
  if (foodCostPct != null) out.foodCostPct = foodCostPct;
  if (laborCostPct != null) out.laborCostPct = laborCostPct;
  if (primeCostPct != null) out.primeCostPct = primeCostPct;
  if (ebitdaPct != null) out.ebitdaPct = ebitdaPct;
  return out;
}

/* ------------------------------------------------------------------ */
/* Lo que la pantalla necesita saber de las ventas                     */
/* ------------------------------------------------------------------ */

/** Ventas sin IVA por mes (YYYY-MM). */
export function ventasPorMes(days: readonly DailyRevenue[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const d of days) {
    const m = monthOf(d.date);
    out[m] = (out[m] ?? 0) + revenueOf(d);
  }
  for (const k of Object.keys(out)) out[k] = round(out[k], 2);
  return out;
}

export interface ContextoObjetivos {
  propuesta?: PropuestaVentas;
  /** «Hoy gastan $12.60»: lo que gasta cada cliente aquí, último mes cerrado con clientes. */
  ticketHoy?: number;
}

/** Propuesta de ventas y ticket por cliente de hoy, de las ventas del último año. */
export function contextoObjetivos(days: readonly DailyRevenue[], mesActual: string): ContextoObjetivos {
  const propuesta = proponerVentas(ventasPorMes(days), mesActual);
  const porMes = new Map<string, { ventas: number; clientes: number }>();
  for (const d of days) {
    const m = monthOf(d.date);
    if (m >= mesActual || !d.customers) continue;
    const b = porMes.get(m) ?? { ventas: 0, clientes: 0 };
    b.ventas += inHouseRevenueOf(d);
    b.clientes += d.customers;
    porMes.set(m, b);
  }
  const ultimo = [...porMes.entries()].filter(([, b]) => b.clientes > 0).sort(([a], [b]) => b.localeCompare(a))[0];
  return {
    ...(propuesta ? { propuesta } : {}),
    ...(ultimo ? { ticketHoy: round(ultimo[1].ventas / ultimo[1].clientes, 2) } : {}),
  };
}
